import React from "react";
import {
  DatePicker,
  Flex,
  Modal,
  List,
  Typography,
  Button,
  Form,
  Input,
  Select,
  Popconfirm,
} from "antd";
import TextArea from "antd/es/input/TextArea";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import { BASE_API_URL } from "../../../../utils/consts";
import axios from "axios";
import dayjs from "dayjs";

const { Text } = Typography;
const { Option } = Select;

type Props = {
  isOpen: boolean;
  onCloseModal: () => void;
  mode?: "create" | "edit";
  modalData?: any;
  selectedMonth: any;
  userId: any;
  onUpdate: () => void;
  userWorkLocations?: any[];
  defaultLocationId?: string;
};

const ShortageOverageModal: React.FC<Props> = ({
  isOpen,
  onCloseModal,
  mode = "create",
  modalData,
  selectedMonth,
  userId,
  onUpdate,
  userWorkLocations = [],
  defaultLocationId,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();

  const handleCancel = () => {
    onCloseModal();
    form.resetFields();
  };

  const onFinish = async (values: any) => {
    const payload = {
      date: dayjs(values.date).startOf("day").format("YYYY-MM-DD"),

      total: values.total,
      type: values.type,
      description: values.description,
      locationId: values.locationId || null,
    };

    if (mode === "create") {
      await axios.post(`${BASE_API_URL}/userPayments/`, {
        userId: userId,
        shortageOverage: payload,
      });
    } else {
      if (!modalData) return;
      await axios.put(
        `${BASE_API_URL}/userPayments/${modalData._id}/${modalData.userId._id}`,
        {
          shortageOverage: payload,
        },
      );
    }

    onUpdate();
    onCloseModal();
    form.resetFields();
  };

  const totalAmount =
    modalData?.shortageOverage?.reduce(
      (acc: number, item: any) => acc + Number(item.total || 0),
      0,
    ) || 0;

  return (
    <Modal
      title={<div>{t("Shortage / Overage")}</div>}
      open={isOpen}
      onCancel={handleCancel}
      footer={[]}
      className="standardHeader"
    >
      {/* LISTA */}
      <List
        dataSource={modalData?.shortageOverage || []}
        locale={{ emptyText: t("No records yet") }}
        renderItem={(item: any) => (
          <List.Item
            style={{
              border: "1px solid #f0f0f0",
              borderRadius: 8,
              marginBottom: 8,
              padding: "8px 12px",
              background: "#fafafa",
            }}
          >
            <Flex
              justify="space-between"
              align="center"
              style={{ width: "100%" }}
            >
              <div>
                <Text strong>{format(new Date(item.date), "dd.MM.yyyy")}</Text>
                <div style={{ fontSize: 12, color: "#777" }}>
                  {item.type === "shortage" ? t("Shortage") : t("Overage")}
                </div>
                {item.description && (
                  <div style={{ fontSize: 12, color: "#777" }}>
                    {item.description}
                  </div>
                )}
              </div>

              {/* DESNO */}
              <Flex align="center" gap="small">
                <Text
                  style={{
                    color: item.type === "shortage" ? "#cf1322" : "#389e0d",
                    fontWeight: 500,
                  }}
                >
                  {item.type === "shortage" ? "-" : "+"}
                  {item.total} RSD
                </Text>

                <Popconfirm
                  title={t("Are you sure you want to delete this record?")}
                  okText={t("Yes")}
                  cancelText={t("No")}
                  onConfirm={async () => {
                    await axios.delete(
                      `${BASE_API_URL}/deleteShortageOverage/${item._id}/${modalData.userId._id}`,
                    );
                    onUpdate();
                  }}
                >
                  <Button danger size="small">
                    {t("Delete")}
                  </Button>
                </Popconfirm>
              </Flex>
            </Flex>
          </List.Item>
        )}
      />

      {/* UKUPNO */}
      {modalData?.shortageOverage?.length > 0 && (
        <Flex justify="end" style={{ marginBottom: 16 }}>
          <Text strong>
            {t("Total")}:{" "}
            <span style={{ color: "#389e0d" }}>
              {totalAmount.toLocaleString()} RSD
            </span>
          </Text>
        </Flex>
      )}

      {/* FORMA */}
      <Form
        form={form}
        labelCol={{ span: 8 }}
        wrapperCol={{ span: 16 }}
        onFinish={onFinish}
        autoComplete="off"
      >
        <Form.Item
          name="date"
          label={t("Date")}
          rules={[{ required: true, message: t("Please select date!") }]}
        >
          <DatePicker placeholder={t("Select date")} />
        </Form.Item>

        {userWorkLocations.length > 0 && (
          <Form.Item
            name="locationId"
            label={t("Work Location")}
            initialValue={defaultLocationId}
            rules={[
              {
                required: true,
                message: t("Please select work location!"),
              },
            ]}
          >
            <Select placeholder={t("Select work location")}>
              {userWorkLocations.map((location: any) => (
                <Option key={location._id} value={location._id}>
                  {location.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        )}

        <Form.Item
          name="type"
          label={t("Type")}
          rules={[{ required: true, message: t("Please select type!") }]}
        >
          <Select placeholder={t("Select type")}>
            <Option value="shortage">{t("Shortage (manjak)")}</Option>
            <Option value="overage">{t("Overage (višak)")}</Option>
          </Select>
        </Form.Item>

        <Form.Item
          name="total"
          label={t("Amount")}
          rules={[{ required: true, message: t("Please enter amount!") }]}
        >
          <Input type="number" placeholder={t("Enter amount")} />
        </Form.Item>

        <Form.Item label={t("Description")} name="description">
          <TextArea rows={2} />
        </Form.Item>

        <Form.Item wrapperCol={{ offset: 8, span: 16 }}>
          <Button type="primary" htmlType="submit">
            {t("Save")}
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ShortageOverageModal;
